import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Bot, ShieldCheck, Zap, Layers } from "lucide-react";
import { useApiVersionLabel } from "@/hooks/useApiVersion";

export default function Login() {
  const apiVersion = useApiVersionLabel();
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = mode === "login" ? await login(email, password) : await register(email, password, name);
    setBusy(false);
    if (!res.ok) toast.error(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden" style={{ background: "linear-gradient(150deg,#0b1220 0%,#0f2942 55%,#0077B5 130%)" }}>
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg,#229ED9,#0077B5)" }}><Bot size={20} /></div>
          <span className="font-bold text-lg">BotControl</span>
        </div>
        <div className="space-y-6 max-w-md">
          <h1 className="text-4xl font-extrabold leading-tight">The control plane for your Telegram bots.</h1>
          <p className="text-slate-300">Create, configure, deploy, monitor and secure thousands of bots — powered by the official Telegram Bot API &amp; Managed Bots.</p>
          <div className="space-y-3 pt-2">
            {[[ShieldCheck, "Envelope-encrypted token vault"], [Zap, "Managed Bots + live provisioning"], [Layers, "Full capability matrix, zero fake actions"]].map(([Ic, t], i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-slate-200"><Ic size={18} className="text-sky-300" /> {t}</div>
            ))}
          </div>
        </div>
        <div className="text-xs text-slate-500 mono" data-testid="login-api-version">Telegram Bot API{apiVersion ? ` ${apiVersion}` : ""} · Official APIs only</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg,#229ED9,#0077B5)" }}><Bot size={20} className="text-white" /></div>
            <span className="font-bold text-lg">BotControl</span>
          </div>
          <h2 className="text-2xl font-bold">{mode === "login" ? "Welcome back" : "Create your account"}</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">{mode === "login" ? "Sign in to your control plane." : "Start managing bots in seconds."}</p>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" data-testid="input-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ada Lovelace" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required data-testid="input-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required data-testid="input-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="btn-submit-auth">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-4 text-center">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button className="text-primary font-medium" data-testid="btn-toggle-mode" onClick={() => setMode(mode === "login" ? "register" : "login")}>
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
